import {
  Building2,
  CreditCard,
  Gauge,
  LogOut,
  ScrollText,
  ShieldCheck,
  Tag,
  Users,
} from 'lucide-react';
import { NavLink, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { ConnexionPage } from '@/features/auth/ConnexionPage';
import DashboardPage from '@/features/dashboard/DashboardPage';
import EntreprisesPage from '@/features/companies/EntreprisesPage';
import UtilisateursPage from '@/features/users/UtilisateursPage';
import AbonnementsPage from '@/features/subscriptions/AbonnementsPage';
import CouponsPage from '@/features/coupons/CouponsPage';
import UsageApiPage from '@/features/apiUsage/UsageApiPage';
import RgpdPage from '@/features/gdpr/RgpdPage';
import JournalAuditPage from '@/features/audit/JournalAuditPage';
import { Logo } from '@/shared/ui/Logo';
import { GardeAdmin } from '@/shared/auth/GardeAdmin';
import { useSession } from '@/shared/auth/sessionStore';
import { cn } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';

/**
 * Navigation de l'outil.
 *
 * Un tableau statique, PAS un registre pilote par l'API comme dans l'app utilisateur.
 * La raison est directe : l'app utilisateur adapte son menu aux modules souscrits par
 * chaque entreprise ; l'administration, elle, a toujours exactement les memes ecrans.
 * Un registre dynamique n'y ajouterait qu'une indirection et un endpoint de plus a
 * proteger.
 */
const ECRANS = [
  { chemin: '/', libelle: 'Tableau de bord', icone: Gauge, exact: true },
  { chemin: '/entreprises', libelle: 'Entreprises', icone: Building2 },
  { chemin: '/utilisateurs', libelle: 'Comptes', icone: Users },
  { chemin: '/abonnements', libelle: 'Abonnements', icone: CreditCard },
  { chemin: '/coupons', libelle: 'Coupons', icone: Tag },
  { chemin: '/usage-api', libelle: 'Usage API', icone: Gauge },
  { chemin: '/rgpd', libelle: 'RGPD', icone: ShieldCheck },
  { chemin: '/journal', libelle: 'Journal d’audit', icone: ScrollText },
] as const;

function Coquille() {
  const email = useSession((etat) => etat.email);
  const deconnecter = useSession((etat) => etat.deconnecter);

  return (
    <div className="flex min-h-dvh bg-fond">
      <aside className="hidden w-52 shrink-0 flex-col border-r border-bordure bg-fond-eleve lg:flex">
        <div className="flex items-center gap-2 border-b border-bordure px-3 py-3">
          <Logo taille="sm" />
          <span className="rounded-full border border-danger/40 bg-danger-voile px-1.5 py-px text-[0.625rem] font-medium text-danger">
            ADMIN
          </span>
        </div>

        <nav className="flex-1 space-y-0.5 p-2" aria-label="Navigation principale">
          {ECRANS.map((ecran) => (
            <NavLink
              key={ecran.chemin}
              to={ecran.chemin}
              end={'exact' in ecran ? ecran.exact : false}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-[--radius-astro] px-2 py-1.5',
                  'transition-colors duration-100',
                  isActive
                    ? 'bg-accent-voile font-medium text-accent'
                    : 'text-texte-doux hover:bg-surface-haute hover:text-texte',
                )
              }
            >
              <ecran.icone className="size-3.5 shrink-0" aria-hidden />
              {ecran.libelle}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-bordure p-2">
          <p className="truncate px-2 text-texte-faible" title={email ?? undefined}>
            {email}
          </p>

          <Bouton
            variante="discret"
            taille="sm"
            className="w-full justify-start"
            onClick={() => void deconnecter()}
          >
            <LogOut />
            Se deconnecter
          </Bouton>
        </div>
      </aside>

      {/* Desktop-first assume (§5) : sous `lg`, la barre laterale devient une barre
          horizontale defilante plutot qu'un tiroir. Un tiroir suppose un geste de plus
          pour naviguer, ce qui n'a de sens que sur un usage mobile — qui n'est pas le
          cas ici. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <nav
          className="flex gap-1 overflow-x-auto border-b border-bordure bg-fond-eleve px-2 py-1.5 lg:hidden"
          aria-label="Navigation principale"
        >
          {ECRANS.map((ecran) => (
            <NavLink
              key={ecran.chemin}
              to={ecran.chemin}
              end={'exact' in ecran ? ecran.exact : false}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap rounded-[--radius-astro] px-2 py-1',
                  isActive ? 'bg-accent-voile text-accent' : 'text-texte-doux',
                )
              }
            >
              {ecran.libelle}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1 p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Routeur.
 *
 * TOUTES LES ROUTES SONT DERRIERE LA GARDE (§1, §3) : elle enveloppe le `Routes` entier
 * plutot que chaque route. Ajouter un ecran ne demande donc rien de plus, et surtout
 * n'offre aucune occasion d'oublier la protection.
 */
export function Routeur() {
  return (
    <GardeAdmin formulaireConnexion={<ConnexionPage />}>
      <Routes>
        <Route element={<Coquille />}>
          <Route index element={<DashboardPage />} />
          <Route path="entreprises" element={<EntreprisesPage />} />
          <Route path="utilisateurs" element={<UtilisateursPage />} />
          <Route path="abonnements" element={<AbonnementsPage />} />
          <Route path="coupons" element={<CouponsPage />} />
          <Route path="usage-api" element={<UsageApiPage />} />
          <Route path="rgpd" element={<RgpdPage />} />
          <Route path="journal" element={<JournalAuditPage />} />

          {/* Pas de page « introuvable » elaboree : un chemin inconnu ramene a l'accueil. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </GardeAdmin>
  );
}
